/*
  Steelpath – gedrag van de site.
  Geen libraries: enkel native <dialog>, IntersectionObserver en CSS-transities.
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Paneel-helper (menu en offertepaneel) ---------- */

  class Panel {
    constructor(dialog) {
      this.dialog = dialog;
      this.opener = null;
      this.closing = false;

      // Escape: native 'cancel'-event, maar we sluiten met animatie.
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      // Klik op de achtergrond (buiten het paneel) sluit.
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inside) this.close();
      });
    }

    get isOpen() {
      return this.dialog.open;
    }

    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener || document.activeElement;
      // showModal() maakt de rest van de pagina inert: focus blijft in het paneel.
      this.dialog.showModal();
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
      this.dialog.dispatchEvent(new CustomEvent('sp:open'));
    }

    close({ instant = false, restoreFocus = true } = {}) {
      if (!this.dialog.open || this.closing) return Promise.resolve();
      this.closing = true;
      this.dialog.dispatchEvent(new CustomEvent('sp:close'));
      this.dialog.classList.remove('is-open');

      return new Promise((resolve) => {
        const finish = () => {
          this.dialog.close();
          this.closing = false;
          if (restoreFocus && this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
          resolve();
        };
        if (instant || reducedMotion.matches) return finish();
        let done = false;
        const once = () => {
          if (done) return;
          done = true;
          finish();
        };
        this.dialog.addEventListener('transitionend', (e) => e.target === this.dialog && once(), { once: true });
        setTimeout(once, 500);
      });
    }
  }

  /* ---------- Header ---------- */

  const header = document.querySelector('[data-sp-header]');
  if (header) {
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 4);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---------- Mobiel menu ---------- */

  const menuDialog = document.querySelector('[data-sp-menu]');
  const menu = menuDialog ? new Panel(menuDialog) : null;
  if (menu) {
    const toggles = document.querySelectorAll('[data-sp-menu-open]');
    const setExpanded = (value) => toggles.forEach((btn) => btn.setAttribute('aria-expanded', String(value)));
    toggles.forEach((btn) => btn.addEventListener('click', () => menu.open(btn)));
    menuDialog.querySelectorAll('[data-sp-menu-close]').forEach((btn) => btn.addEventListener('click', () => menu.close()));
    menuDialog.addEventListener('sp:open', () => setExpanded(true));
    menuDialog.addEventListener('sp:close', () => setExpanded(false));
    menuDialog.querySelectorAll('a[href]').forEach((link) =>
      link.addEventListener('click', () => menu.close({ instant: true, restoreFocus: false }))
    );
  }

  /* ---------- Onthullen bij scrollen ---------- */

  const revealItems = document.querySelectorAll('[data-sp-reveal]');
  if ('IntersectionObserver' in window && !reducedMotion.matches) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    revealItems.forEach((item) => observer.observe(item));
  } else {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  }

  /* ---------- Hero-video: pas laden na de pagina, nooit bij beperkte beweging of databesparing ---------- */

  const videoTemplate = document.querySelector('[data-sp-hero-video]');
  const saveData = navigator.connection && navigator.connection.saveData;
  if (videoTemplate && !reducedMotion.matches && !saveData) {
    const loadVideo = () => {
      const media = videoTemplate.closest('.sp-hero__media');
      const fragment = videoTemplate.content.cloneNode(true);
      const video = fragment.querySelector('video');
      if (!video || !media) return;
      video.muted = true;
      video.setAttribute('aria-hidden', 'true');
      video.preload = 'auto';
      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
      media.querySelector('.sp-media').appendChild(fragment);
      video.play().catch(() => {});
    };
    const whenVisible = () => {
      if (!('IntersectionObserver' in window)) return loadVideo();
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          loadVideo();
        }
      });
      io.observe(videoTemplate.parentElement);
    };
    if (document.readyState === 'complete') whenVisible();
    else window.addEventListener('load', whenVisible, { once: true });
  }

  /* ---------- Mobiel (tot 1023 px): inklappen, meer tonen, veegbare lijsten ---------- */

  const mobile = window.matchMedia('(max-width: 1023px)');
  const chevron =
    '<svg class="sp-icon sp-collapse__chevron" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>';
  let uid = 0;
  const ensureId = (el, prefix) => {
    if (!el.id) {
      uid += 1;
      el.id = `${prefix}-${uid}`;
    }
    return el.id;
  };

  // Inklapbare blokken: [data-sp-collapse] > [data-sp-collapse-heading] + [data-sp-collapse-panel].
  // Alleen op mobiel wordt de kop een knop; zonder JavaScript of op desktop blijft alles open.
  const collapsibles = Array.from(document.querySelectorAll('[data-sp-collapse]')).map((wrap) => {
    const heading = wrap.querySelector('[data-sp-collapse-heading]');
    const panel = wrap.querySelector('[data-sp-collapse-panel]');
    return { wrap, heading, panel, original: heading ? heading.innerHTML : '', open: wrap.hasAttribute('data-sp-collapse-open') };
  });

  const applyCollapse = (item) => {
    const { wrap, heading, panel } = item;
    if (!heading || !panel) return;
    if (mobile.matches) {
      if (heading.querySelector('.sp-collapse__toggle')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sp-collapse__toggle';
      button.setAttribute('aria-expanded', String(item.open));
      button.setAttribute('aria-controls', ensureId(panel, 'SpCollapse'));
      button.innerHTML = `<span class="sp-collapse__label">${item.original}</span>${chevron}`;
      button.addEventListener('click', () => {
        item.open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(item.open));
        panel.classList.toggle('sp-is-collapsed', !item.open);
        wrap.classList.toggle('is-open', item.open);
      });
      heading.replaceChildren(button);
      panel.classList.toggle('sp-is-collapsed', !item.open);
      wrap.classList.toggle('is-open', item.open);
      wrap.classList.add('sp-collapse--active');
    } else {
      heading.innerHTML = item.original;
      panel.classList.remove('sp-is-collapsed');
      wrap.classList.remove('sp-collapse--active', 'is-open');
    }
  };

  // Meer tonen: [data-sp-more] met data-sp-more-count (zichtbaar op mobiel) en data-sp-more-label.
  // Items zijn [data-sp-more-item] of anders de directe kinderen.
  const moreLists = Array.from(document.querySelectorAll('[data-sp-more]')).map((list) => {
    const marked = list.querySelectorAll('[data-sp-more-item]');
    const items = Array.from(marked.length ? marked : list.children);
    return { list, items, count: parseInt(list.dataset.spMoreCount, 10) || 3, button: null, expanded: false };
  });

  const applyMore = (entry) => {
    const { list, items, count } = entry;
    const extra = items.length - count;
    if (!mobile.matches || extra <= 0) {
      items.forEach((el) => el.classList.remove('sp-is-more-hidden'));
      if (entry.button) entry.button.hidden = true;
      return;
    }
    if (!entry.button) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sp-more-button';
      button.setAttribute('aria-controls', ensureId(list, 'SpMore'));
      button.addEventListener('click', () => {
        entry.expanded = !entry.expanded;
        render();
        if (entry.expanded) {
          const first = items[count];
          const focusable = first && first.querySelector('a, button');
          if (focusable) focusable.focus({ preventScroll: true });
        }
      });
      const anchor = list.closest('[data-sp-more-anchor]') || list;
      anchor.insertAdjacentElement('afterend', button);
      entry.button = button;
    }
    const label = list.dataset.spMoreLabel || 'items';
    const render = () => {
      items.forEach((el, i) => {
        const hide = i >= count && !entry.expanded;
        el.classList.toggle('sp-is-more-hidden', hide);
        if (!hide && i >= count) el.classList.add('sp-is-more-shown');
      });
      entry.button.hidden = false;
      entry.button.setAttribute('aria-expanded', String(entry.expanded));
      // Eigen knoptekst via data-sp-more-text / data-sp-more-less (bv. "Lees verder").
      if (list.dataset.spMoreText) {
        entry.button.innerHTML = `${entry.expanded ? list.dataset.spMoreLess || 'Minder tonen' : list.dataset.spMoreText}${chevron}`;
      } else {
        entry.button.innerHTML = entry.expanded
          ? `Toon minder ${label}${chevron}`
          : `Toon alle ${label} <span class="sp-more-button__count">(${extra} meer)</span>${chevron}`;
      }
    };
    entry.render = render;
    render();
  };

  // Veegbare lijst met vorige/volgende-knoppen: [data-sp-carousel] > [data-sp-carousel-track].
  const carousels = Array.from(document.querySelectorAll('[data-sp-carousel]')).map((root) => {
    const track = root.querySelector('[data-sp-carousel-track]');
    const nav = root.querySelector('[data-sp-carousel-nav]');
    const prev = root.querySelector('[data-sp-carousel-prev]');
    const next = root.querySelector('[data-sp-carousel-next]');
    const status = root.querySelector('[data-sp-carousel-status]');
    const slides = Array.from(track ? track.children : []);
    // Scrollpositie van een kaart: afstand tot de eerste kaart (de strook is zelf de offsetParent).
    const position = (slide) => slide.offsetLeft - slides[0].offsetLeft;
    const current = () => {
      const left = track.scrollLeft;
      let index = 0;
      slides.forEach((s, i) => {
        if (Math.abs(position(s) - left) < Math.abs(position(slides[index]) - left)) index = i;
      });
      return index;
    };
    const go = (i) => {
      const target = slides[Math.max(0, Math.min(i, slides.length - 1))];
      track.scrollTo({ left: position(target), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    const update = () => {
      if (!track || !slides.length) return;
      const scrollable = track.scrollWidth > track.clientWidth + 4;
      nav.hidden = !scrollable;
      if (!scrollable) return;
      const i = current();
      prev.disabled = track.scrollLeft < 4;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      status.textContent = `${i + 1} / ${slides.length}`;
    };
    if (track) {
      prev.addEventListener('click', () => go(current() - 1));
      next.addEventListener('click', () => go(current() + 1));
      let ticking = false;
      track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            update();
          });
        },
        { passive: true }
      );
    }
    return { update };
  });

  const applyMobile = () => {
    collapsibles.forEach(applyCollapse);
    moreLists.forEach(applyMore);
    carousels.forEach((c) => c.update());
  };
  applyMobile();
  mobile.addEventListener('change', applyMobile);
  window.addEventListener('resize', () => carousels.forEach((c) => c.update()), { passive: true });

  /* ---------- Zo werkt het: stalen platen die één voor één gelegd worden tijdens het scrollen ---------- */

  document.querySelectorAll('[data-sp-steps]').forEach((wrap) => {
    const road = wrap.querySelector('[data-sp-steps-road]');
    const markers = Array.from(wrap.querySelectorAll('[data-sp-step-marker]'));
    wrap.style.setProperty('--sp-step-count', markers.length);
    if (!road || markers.length < 2) {
      markers.forEach((m) => m.classList.add('is-active'));
      return;
    }

    let plates = [];
    let fractions = [];

    // Golvende rijweg door de middelpunten van de stapcirkels: tussen twee stappen telkens een halve golf,
    // afwisselend naar boven en naar beneden (of links en rechts als de stappen onder elkaar staan).
    // Elke plaat ligt op de curve en draait mee met de raaklijn.
    const build = () => {
      const box = wrap.getBoundingClientRect();
      const points = markers.map((m) => {
        const r = m.getBoundingClientRect();
        return { x: r.left - box.left + r.width / 2, y: r.top - box.top + r.height / 2 };
      });
      const horizontal = Math.abs(points[points.length - 1].y - points[0].y) < 4;
      const amplitude = horizontal ? 22 : 14;
      const plateLength = horizontal ? 50 : 40;
      const plateWidth = horizontal ? 26 : 22;
      const gap = 4;

      // Curve bemonsteren en de afgelegde lengte bijhouden.
      const samples = [];
      const markerLengths = [0];
      let total = 0;
      for (let i = 0; i < points.length - 1; i += 1) {
        const a = points[i];
        const b = points[i + 1];
        const side = i % 2 === 0 ? -1 : 1;
        for (let k = i === 0 ? 0 : 1; k <= 60; k += 1) {
          const t = k / 60;
          const wave = side * amplitude * Math.sin(Math.PI * t);
          const point = horizontal
            ? { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t + wave }
            : { x: a.x + (b.x - a.x) * t + wave, y: a.y + (b.y - a.y) * t };
          const prev = samples[samples.length - 1];
          if (prev) total += Math.hypot(point.x - prev.x, point.y - prev.y);
          point.d = total;
          samples.push(point);
        }
        markerLengths.push(total);
      }
      fractions = markerLengths.map((d) => d / (total || 1));

      const count = Math.max(markers.length * 2, Math.floor(total / (plateLength + gap)));
      const pitch = total / count;
      if (count !== plates.length) {
        road.textContent = '';
        plates = Array.from({ length: count }, () => {
          const plate = document.createElement('span');
          plate.className = 'sp-steps__plate';
          road.appendChild(plate);
          return plate;
        });
      }
      road.style.setProperty('--sp-road-plate-length', `${pitch - gap}px`);
      road.style.setProperty('--sp-road-plate-width', `${plateWidth}px`);

      let j = 0;
      plates.forEach((plate, i) => {
        const d = (i + 0.5) * pitch;
        while (j < samples.length - 2 && samples[j + 1].d < d) j += 1;
        const a = samples[j];
        const b = samples[j + 1];
        const t = (d - a.d) / ((b.d - a.d) || 1);
        const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
        plate.style.left = `${(a.x + (b.x - a.x) * t).toFixed(1)}px`;
        plate.style.top = `${(a.y + (b.y - a.y) * t).toFixed(1)}px`;
        plate.style.setProperty('--sp-road-angle', `${angle.toFixed(1)}deg`);
      });
      update();
    };

    const update = () => {
      let progress = 1;
      if (!reducedMotion.matches) {
        const r = wrap.getBoundingClientRect();
        const vh = window.innerHeight;
        const span = Math.max(r.height, vh * 0.45);
        progress = Math.min(Math.max((vh * 0.8 - r.top) / span, 0), 1);
      }
      const n = plates.length;
      plates.forEach((plate, i) => plate.classList.toggle('is-laid', progress >= (i + 0.5) / n));
      markers.forEach((m, i) => m.classList.toggle('is-active', progress >= fractions[i] - 0.001));
    };

    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            update();
          });
        }
      },
      { passive: true }
    );
    reducedMotion.addEventListener && reducedMotion.addEventListener('change', update);
    if ('ResizeObserver' in window) new ResizeObserver(build).observe(wrap);
    else window.addEventListener('resize', build);
    build();
  });

  /* ---------- 3D-plaat: langzaam draaien, slepen (muis en touch), pijltjestoetsen, gedempt kantelen met de muis ---------- */

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  document.querySelectorAll('[data-sp-plate3d]').forEach((plate) => {
    const tilt = plate.querySelector('.sp-plate3d__tilt');
    const SPEED = 12; // graden per seconde: een rondje in 30 s
    const MAX_TILT = 18;
    let angle = -30;
    let visible = !('IntersectionObserver' in window);
    let drag = null;
    let target = { x: 0, y: 0 };
    let current = { x: 0, y: 0 };
    let frame = null;
    let last = 0;

    plate.classList.add('is-interactive');

    const render = () => {
      plate.style.setProperty('--sp-spin', `${angle.toFixed(2)}deg`);
      tilt.style.setProperty('--sp-tilt-x', `${current.x.toFixed(2)}deg`);
      tilt.style.setProperty('--sp-tilt-y', `${current.y.toFixed(2)}deg`);
    };

    const tick = (now) => {
      const dt = last ? Math.min(now - last, 100) / 1000 : 0;
      last = now;
      const autoSpin = !reducedMotion.matches && !drag;
      if (autoSpin) angle = (angle + SPEED * dt) % 360;
      current.x += (target.x - current.x) * 0.08;
      current.y += (target.y - current.y) * 0.08;
      render();
      const settled = Math.abs(target.x - current.x) < 0.02 && Math.abs(target.y - current.y) < 0.02;
      frame = visible && (autoSpin || !settled) ? requestAnimationFrame(tick) : null;
      if (!frame) last = 0;
    };
    const kick = () => {
      if (!frame && visible) frame = requestAnimationFrame(tick);
    };

    if (!visible) {
      new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting;
        plate.classList.toggle('is-paused', !visible);
        kick();
      }).observe(plate);
    }
    reducedMotion.addEventListener && reducedMotion.addEventListener('change', kick);
    render();
    kick();

    // Slepen: horizontaal draait de plaat; verticaal scrollen blijft werken (touch-action: pan-y).
    plate.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      drag = { x: event.clientX, angle };
      plate.classList.add('is-dragging');
      try {
        plate.setPointerCapture(event.pointerId);
      } catch (error) {
        // Geen capture mogelijk: slepen werkt dan zolang de aanwijzer op de plaat blijft.
      }
    });
    plate.addEventListener('pointermove', (event) => {
      if (!drag) return;
      angle = drag.angle + (event.clientX - drag.x) * 0.5;
      render();
    });
    const endDrag = () => {
      if (!drag) return;
      drag = null;
      plate.classList.remove('is-dragging');
      kick(); // draait daarna langzaam verder vanaf de nieuwe hoek
    };
    plate.addEventListener('pointerup', endDrag);
    plate.addEventListener('pointercancel', endDrag);
    plate.addEventListener('lostpointercapture', endDrag);

    // Toetsenbord: pijltjes links en rechts draaien de plaat.
    plate.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      angle += event.key === 'ArrowLeft' ? -15 : 15;
      render();
    });

    // Kantelen volgt de muis over de hele kaart (ook over de tekst ernaast), tot ± 18°, gedempt.
    const area = plate.closest('.sp-plate, .sp-product__grid') || plate;
    area.addEventListener('pointermove', (event) => {
      if (!finePointer.matches || reducedMotion.matches || event.pointerType !== 'mouse') return;
      const r = plate.getBoundingClientRect();
      const dx = Math.max(-1, Math.min(1, (event.clientX - (r.left + r.width / 2)) / (r.width / 2)));
      const dy = Math.max(-1, Math.min(1, (event.clientY - (r.top + r.height / 2)) / (r.height / 2)));
      target = { x: dy * MAX_TILT, y: dx * MAX_TILT };
      kick();
    });
    area.addEventListener('pointerleave', () => {
      target = { x: 0, y: 0 };
      kick();
    });
  });

  /* ---------- Realisatie: groot-weergave van de collage (native dialog, pijltjes en Escape) ---------- */

  document.querySelectorAll('[data-sp-collage]').forEach((collage) => {
    const dialog = collage.parentElement.querySelector('[data-sp-lightbox]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const buttons = Array.from(collage.querySelectorAll('[data-sp-collage-index]'));
    const img = dialog.querySelector('[data-sp-lightbox-img]');
    const caption = dialog.querySelector('[data-sp-lightbox-caption]');
    const closeBtn = dialog.querySelector('[data-sp-lightbox-close]');
    let index = 0;
    let opener = null;

    const show = (i) => {
      index = (i + buttons.length) % buttons.length;
      const btn = buttons[index];
      img.src = btn.dataset.spFull;
      img.alt = btn.dataset.spAlt || '';
      caption.textContent = `Foto ${index + 1} van ${buttons.length}${btn.dataset.spAlt ? ` – ${btn.dataset.spAlt}` : ''}`;
    };

    buttons.forEach((btn, i) => {
      btn.addEventListener('click', () => {
        opener = btn;
        // De "+N"-tegel (zesde foto) opent de eerste foto die niet in de collage staat.
        const startAt = btn.querySelector('.sp-collage__more') ? 6 : i;
        show(startAt);
        dialog.showModal();
        closeBtn.focus();
      });
    });

    const prev = dialog.querySelector('[data-sp-lightbox-prev]');
    const next = dialog.querySelector('[data-sp-lightbox-next]');
    if (prev) prev.addEventListener('click', () => show(index - 1));
    if (next) next.addEventListener('click', () => show(index + 1));
    closeBtn.addEventListener('click', () => dialog.close());
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        show(index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        show(index + 1);
      }
    });
    // Klik naast de foto sluit; Escape sluit via de native dialog.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      img.removeAttribute('src');
      if (opener && opener.isConnected) opener.focus();
    });
  });

  /* ---------- Veelgestelde vragen: onderwerpen op mobiel (maximaal één tegelijk open) ---------- */

  document.querySelectorAll('[data-sp-faq-topics]').forEach((list) => {
    const toggles = Array.from(list.querySelectorAll('[data-sp-faq-topic-toggle]'));
    const setOpen = (toggle, open) => {
      toggle.setAttribute('aria-expanded', String(open));
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (panel) panel.classList.toggle('is-open', open);
    };
    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggles.forEach((other) => setOpen(other, other === toggle ? open : false));
      });
    });
  });

  /* ---------- Accordeon (veelgestelde vragen) ---------- */

  document.querySelectorAll('[data-sp-accordion]').forEach((accordion) => {
    const toggles = Array.from(accordion.querySelectorAll('[data-sp-accordion-toggle]'));
    toggles.forEach((toggle) => {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        panel.classList.toggle('is-open', open);
      });
      // Pijltjestoetsen, Home en End tussen de vragen.
      toggle.addEventListener('keydown', (event) => {
        // Alleen zichtbare vragen (op mobiel kunnen er achter 'Toon alle vragen' zitten).
        const visible = toggles.filter((t) => t.offsetParent !== null);
        const at = visible.indexOf(toggle);
        const keys = { ArrowDown: at + 1, ArrowUp: at - 1, Home: 0, End: visible.length - 1 };
        if (!(event.key in keys) || at === -1) return;
        event.preventDefault();
        visible[(keys[event.key] + visible.length) % visible.length].focus();
      });
    });
  });

  /* ---------- Juridische pagina's: inhoudsopgave uit de H2-koppen (vanaf 3 koppen) ---------- */

  document.querySelectorAll('[data-sp-legal]').forEach((legal) => {
    const body = legal.querySelector('[data-sp-legal-body]');
    const toc = legal.querySelector('[data-sp-legal-toc]');
    const list = legal.querySelector('[data-sp-legal-toc-list]');
    if (!body || !toc || !list) return;
    const headings = Array.from(body.querySelectorAll('h2')).filter((h) => h.textContent.trim());
    if (headings.length < 3) return;
    const used = new Set(Array.from(document.querySelectorAll('[id]')).map((el) => el.id));
    headings.forEach((heading, i) => {
      if (!heading.id) {
        const base =
          heading.textContent
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 60) || `deel-${i + 1}`;
        let id = base;
        for (let n = 2; used.has(id); n += 1) id = `${base}-${n}`;
        used.add(id);
        heading.id = id;
      }
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.append(link);
      list.append(item);
    });
    toc.hidden = false;
  });

  /* ---------- Brochure ---------- */

  // De PDF met fetch downloaden als "Steelpath-brochure.pdf" (het download-attribuut werkt niet voor een link op
  // een ander domein, zoals cdn.shopify.com). Bij een fout opent de link in een nieuw tabblad.
  document.addEventListener('click', async (event) => {
    const link = event.target.closest && event.target.closest('[data-sp-brochure]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (link.getAttribute('aria-busy') === 'true') return;
    const url = link.href;
    link.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const save = document.createElement('a');
      save.href = objectUrl;
      save.download = 'Steelpath-brochure.pdf';
      save.hidden = true;
      document.body.append(save);
      save.click();
      save.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (e) {
      // Nieuw tabblad (zonder verwijzing terug naar deze pagina); houdt de pop-upblokkering het tegen, dan in
      // hetzelfde tabblad.
      const opened = window.open(url, '_blank');
      if (opened) opened.opener = null;
      else window.location.href = url;
    } finally {
      link.removeAttribute('aria-busy');
    }
  });

  /* ---------- Offertepaneel ---------- */

  const quoteDialog = document.querySelector('[data-sp-quote]');
  if (!quoteDialog) return;

  const quote = new Panel(quoteDialog);
  const form = quoteDialog.querySelector('[data-sp-quote-form]');
  const steps = Array.from(quoteDialog.querySelectorAll('[data-sp-step]'));
  const progressSteps = Array.from(quoteDialog.querySelectorAll('[data-sp-progress-step]'));
  const progressBar = quoteDialog.querySelector('[data-sp-progress-bar]');
  const prevBtn = quoteDialog.querySelector('[data-sp-prev]');
  const nextBtn = quoteDialog.querySelector('[data-sp-next]');
  const submitBtn = quoteDialog.querySelector('[data-sp-submit]');
  const status = quoteDialog.querySelector('[data-sp-step-status]');
  const success = quoteDialog.querySelector('[data-sp-quote-success]');
  const formError = quoteDialog.querySelector('[data-sp-quote-error]');
  const tab = document.querySelector('[data-sp-quote-tab]');
  const STORAGE_KEY = 'steelpath-offerte';
  const KEEP_NAMES = ['contact[name]', 'contact[email]', 'contact[phone]', 'contact[body]', 'form_type', 'utf8'];
  let current = 1;

  const storage = {
    get() {
      try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      } catch (e) {
        return null;
      }
    },
    set(value) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch (e) {
        /* opslag niet beschikbaar: geen probleem */
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* idem */
      }
    },
  };

  const fields = () =>
    form
      ? Array.from(form.elements).filter(
          (el) => el.name && !['contact[body]', 'contact[Onderwerp]', 'form_type', 'utf8'].includes(el.name) && el.type !== 'hidden'
        )
      : [];

  // Tijdens het verzenden staan de extra velden zonder name (zie submit); dan niet opslaan.
  let namesStripped = false;

  const saveState = () => {
    if (!form || success || namesStripped) return;
    const data = { step: current, values: {} };
    fields().forEach((el) => {
      if (el.type === 'radio' || el.type === 'checkbox') data.values[`${el.name}::${el.value}`] = el.checked;
      else data.values[el.name] = el.value;
    });
    storage.set(data);
  };

  // Plaattype uit een knop (bv. de producttitel "Rijplaat 5000 × 1500 × 10 mm") koppelen aan de bestaande keuze
  // "5000 × 1500 × 10 mm", zodat elk type maar één keer in de lijst staat. Enkel een echt nieuw type wordt toegevoegd.
  const plateKey = (text) => (text || '').toLowerCase().replace(/rijplaat|stalen/g, '').replace(/[x*]/g, '×').replace(/\s+/g, '');
  const addPlateOption = (select, plate) => {
    const key = plateKey(plate);
    const match = [...select.options].find((o) => o.value === plate || (key && plateKey(o.value) === key));
    if (match) return match.value;
    select.add(new Option(plate, plate), 0);
    return plate;
  };

  const restoreState = () => {
    const data = storage.get();
    if (!form || !data || !data.values) return;
    fields().forEach((el) => {
      if (el.type === 'radio' || el.type === 'checkbox') {
        const key = `${el.name}::${el.value}`;
        if (key in data.values) el.checked = data.values[key];
      } else if (el.type !== 'hidden' && el.name in data.values) {
        // Verborgen velden (bv. het enige plaattype) komen altijd uit de pagina, niet uit een oude sessie.
        el.value = el.tagName === 'SELECT' ? addPlateOption(el, data.values[el.name]) : data.values[el.name];
      }
    });
    current = Math.min(Math.max(parseInt(data.step, 10) || 1, 1), steps.length || 1);
  };

  /* Afhankelijke velden */

  const amountInput = form && form.querySelector('#SpQ-amount');
  const amountSuffix = form && form.querySelector('[data-sp-amount-suffix]');
  const amountLabel = form && form.querySelector('label[for="SpQ-amount"]');
  const syncAmountUnit = () => {
    const unit = form.querySelector('[data-sp-amount-unit]:checked');
    if (!unit || !amountInput) return;
    const isArea = unit.value !== 'platen';
    amountSuffix.textContent = unit.value;
    amountLabel.textContent = isArea ? 'Oppervlakte in m²' : 'Aantal platen';
    amountInput.placeholder = isArea ? 'Bv. 150' : 'Bv. 10';
  };

  const companyField = form && form.querySelector('[data-sp-company-field]');
  // Btw- of ondernemingsnummer: lichte controle. Na het weghalen van "BE", punten, spaties en streepjes
  // moeten er minstens 9 cijfers overblijven (en niets anders).
  const vatInput = form && form.querySelector('[data-sp-vat]');
  const vatError = form && form.querySelector('[data-sp-vat-error]');
  const checkVat = (show = false) => {
    if (!vatInput) return true;
    const raw = vatInput.value.trim();
    const digits = raw.toUpperCase().replace(/^BE/, '').replace(/[.\s-]/g, '');
    const ok = vatInput.disabled || !raw || /^\d{9,}$/.test(digits);
    const missing = !vatInput.disabled && vatInput.required && !raw;
    const message = missing
      ? 'Vul je btw- of ondernemingsnummer in.'
      : 'Vul een geldig btw- of ondernemingsnummer in, met minstens 9 cijfers (bv. BE 0123.456.789).';
    vatInput.setCustomValidity(ok ? '' : message);
    if (vatError) {
      if (show && (missing || !ok)) {
        vatError.textContent = message;
        vatError.hidden = false;
      } else if (ok && !missing) {
        vatError.hidden = true;
      }
    }
    return ok && !missing;
  };
  // Bedrijf: bedrijfsnaam en btw- of ondernemingsnummer verplicht. Particulier: beide verborgen en uitgeschakeld,
  // zodat ze niet meetellen in de validatie (de ingevulde waarden blijven wel bewaard).
  const syncCustomerType = () => {
    const type = form.querySelector('[data-sp-customer-type]:checked');
    if (!companyField || !type) return;
    const isCompany = type.value === 'Bedrijf';
    companyField.hidden = !isCompany;
    companyField.querySelectorAll('input').forEach((el) => {
      el.disabled = !isCompany;
      if (!isCompany) el.removeAttribute('aria-invalid');
    });
    companyField.querySelectorAll('[data-sp-company-required]').forEach((el) => {
      el.required = isCompany;
    });
    companyField.querySelectorAll('[data-sp-company-req]').forEach((marker) => {
      marker.hidden = !isCompany;
    });
    checkVat();
  };

  const toInput = form && form.querySelector('#SpQ-to');
  const fromInput = form && form.querySelector('#SpQ-from');
  const noEnd = form && form.querySelector('[data-sp-no-end]');
  const syncDates = () => {
    if (!toInput) return;
    toInput.disabled = noEnd && noEnd.checked;
  };

  // Diensten: één verplichte keuze uit drie radio's. Zonder keuze een duidelijke melding onder de opties.
  const serviceInputs = form ? Array.from(form.querySelectorAll('[data-sp-service]')) : [];
  const servicesError = form && form.querySelector('[data-sp-services-error]');
  const SERVICES_MESSAGE = 'Kies hoe je de platen wilt krijgen en terugbrengen.';
  const chosenService = () => serviceInputs.find((el) => el.checked) || null;
  const checkServices = (show = false) => {
    if (!serviceInputs.length) return true;
    const message = chosenService() ? '' : SERVICES_MESSAGE;
    serviceInputs.forEach((el) => el.setCustomValidity(message));
    if (servicesError) {
      if (message && show) {
        servicesError.textContent = message;
        servicesError.hidden = false;
      } else if (!message) {
        servicesError.hidden = true;
      }
    }
    return !message;
  };

  // Info-iconen (ⓘ): tikken, Enter of spatie opent het tekstvak onder het veld; opnieuw tikken, Escape of
  // tikken ernaast sluit het. Altijd type="button": het icoon verzendt of blokkeert het formulier nooit.
  const infoButtons = form ? Array.from(form.querySelectorAll('[data-sp-info]')) : [];
  const infoPanel = (btn) => document.getElementById(btn.getAttribute('aria-controls'));
  const setInfo = (btn, open) => {
    const panel = infoPanel(btn);
    if (!panel) return;
    btn.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    if (open) panel.scrollIntoView({ block: 'nearest' });
  };
  const openInfo = () => infoButtons.find((btn) => btn.getAttribute('aria-expanded') === 'true');
  infoButtons.forEach((btn) => {
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      const open = btn.getAttribute('aria-expanded') !== 'true';
      infoButtons.forEach((other) => other !== btn && setInfo(other, false));
      setInfo(btn, open);
    });
  });
  if (infoButtons.length) {
    // Escape sluit eerst het tekstvak, niet het hele offertepaneel.
    window.addEventListener(
      'keydown',
      (event) => {
        const btn = event.key === 'Escape' && openInfo();
        if (!btn) return;
        event.preventDefault();
        event.stopPropagation();
        setInfo(btn, false);
        btn.focus();
      },
      true
    );
    document.addEventListener('pointerdown', (event) => {
      const btn = openInfo();
      if (!btn || btn.contains(event.target) || infoPanel(btn).contains(event.target)) return;
      setInfo(btn, false);
    });
  }

  /* Stappen */

  const stepTitle = (n) => {
    const legend = steps[n - 1] && steps[n - 1].querySelector('legend');
    return legend ? legend.textContent.trim() : '';
  };

  const showStep = (n, { focus = false } = {}) => {
    if (!steps.length) return;
    current = n;
    infoButtons.forEach((btn) => setInfo(btn, false));
    steps.forEach((step, i) => {
      const active = i + 1 === n;
      step.hidden = !active;
      step.classList.toggle('is-entering', active && focus && !reducedMotion.matches);
    });
    progressSteps.forEach((item, i) => {
      if (i + 1 === n) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
      item.classList.toggle('is-done', i + 1 < n);
    });
    if (progressBar) progressBar.style.width = `${(n / steps.length) * 100}%`;
    prevBtn.hidden = n === 1;
    nextBtn.hidden = n === steps.length;
    submitBtn.hidden = n !== steps.length;
    if (status) status.textContent = `Stap ${n} van ${steps.length}: ${stepTitle(n)}`;
    if (focus) {
      const first = steps[n - 1].querySelector('input:not([type="hidden"]):not(:disabled), select, textarea');
      if (first) first.focus({ preventScroll: true });
      form.scrollTop = 0;
    }
  };

  const validateStep = (n) => {
    const step = steps[n - 1];
    if (!step) return true;
    let firstInvalid = null;
    checkVat(true);
    checkPeriod(true);
    checkServices(true);
    step.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el.disabled || el.closest('[hidden]') !== step.closest('[hidden]')) return;
      const valid = el.checkValidity();
      if (el.type !== 'radio' && el.type !== 'checkbox') el.setAttribute('aria-invalid', String(!valid));
      if (!valid && !firstInvalid) firstInvalid = el;
    });
    if (firstInvalid) {
      if (step.hidden) showStep(n);
      firstInvalid.reportValidity();
      firstInvalid.focus();
      return false;
    }
    return true;
  };

  /* Alles samenvoegen in contact[body] */

  const val = (name) => {
    const el = form.elements.namedItem(name);
    if (!el) return '';
    if (el instanceof RadioNodeList) return el.value || '';
    if (el.type === 'checkbox') return el.checked ? el.value : '';
    return (el.value || '').trim();
  };
  const date = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

  /* Minimums (aantal platen, huurperiode) en richtprijs */

  let estimateConfig = {};
  try {
    const configEl = quoteDialog.querySelector('[data-sp-estimate-config]');
    estimateConfig = JSON.parse(configEl ? configEl.textContent : '{}') || {};
  } catch (e) {
    estimateConfig = {};
  }
  // Instellingen zijn tekst: komma of punt als decimaalteken. Leeg of ongeldig = null.
  const num = (value) => {
    if (value === null || value === undefined) return null;
    const n = parseFloat(String(value).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };
  const round2 = (n) => Math.round(n * 100) / 100;
  const minDays = Math.max(0, Math.round(num(estimateConfig.minDays) || 0));
  const minPlates = num(estimateConfig.minPlates);
  const plateArea = num(estimateConfig.plateArea);
  const DAY = 86400000;
  const isoTime = (iso) => {
    const parts = (iso || '').split('-').map(Number);
    return parts.length === 3 && parts[0] ? Date.UTC(parts[0], parts[1] - 1, parts[2]) : null;
  };

  const amountUnit = () => (form.querySelector('[data-sp-amount-unit]:checked') || {}).value || 'platen';
  // Aantal platen: zoals ingevuld, of de oppervlakte gedeeld door het plaatoppervlak, naar boven afgerond.
  const plateCount = () => {
    const raw = num(amountInput && amountInput.value);
    if (!raw || raw <= 0) return null;
    if (amountUnit() === 'platen') return Math.ceil(raw);
    return plateArea ? Math.ceil(round2(raw / plateArea)) : null;
  };
  // Huurdagen: begin- en einddag tellen allebei mee. Geen einddatum (of "nog niet gekend") = null.
  const rentalDays = () => {
    if (!fromInput || !toInput || toInput.disabled) return null;
    const from = isoTime(fromInput.value);
    const to = isoTime(toInput.value);
    if (from === null || to === null) return null;
    return Math.round((to - from) / DAY) + 1;
  };

  // Minima (huurperiode, aantal platen) zijn aanrekeningsminima: de klant mag elke duur en elk aantal invullen.
  // Alleen een einddatum vóór de startdatum geeft een melding.
  const periodError = form && form.querySelector('[data-sp-period-error]');
  const checkPeriod = (show = false) => {
    if (!toInput) return true;
    const days = rentalDays();
    const message = days !== null && days < 1 ? 'De einddatum ligt vóór de startdatum.' : '';
    toInput.setCustomValidity(message);
    if (periodError) {
      if (message && show) {
        periodError.textContent = message;
        periodError.hidden = false;
      } else if (!message) {
        periodError.hidden = true;
      }
    }
    return !message;
  };

  const estimateBox = form && estimateConfig.enabled ? form.querySelector('[data-sp-estimate]') : null;
  const estimateResult = estimateBox && estimateBox.querySelector('[data-sp-estimate-result]');
  // Regel voor het bericht; leeg zolang er geen actuele richtprijs is.
  let estimateLine = '';

  const moneyWhole = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
  const moneyCents = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
  const money = (n) => (Number.isInteger(round2(n)) ? moneyWhole : moneyCents).format(round2(n));
  const range = (lo, hi) => `tussen ${money(lo)} en ${money(hi)}`;

  // Rijtijd heen en terug per lading zonder Google-sleutel (of als Google faalt): een vaste waarde, voor de
  // onder- en de bovenkant.
  const fallbackDrive = () => {
    const hours = num(estimateConfig.driveFallback);
    return hours === null ? null : [hours, hours];
  };

  // Echte rijtijd via de Google Routes API (enkel met een sleutel in de thema-instellingen). Resultaat per adres
  // onthouden in de sessie; time-out 6 seconden. Geeft { seconds, meters } of null (niet gevonden, fout, time-out).
  const ROUTE_TIMEOUT = 6000;
  const routeKey = (address) => `steelpath-rit:${estimateConfig.depot || ''}|${address.toLowerCase().replace(/\s+/g, ' ')}`;
  const fetchRoute = async (address) => {
    const key = String(estimateConfig.mapsKey || '').trim();
    const depot = String(estimateConfig.depot || '').trim();
    if (!key || !depot || !address) return null;
    const cacheKey = routeKey(address);
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached) return cached.seconds ? cached : null;
    } catch (e) {
      // sessionStorage niet beschikbaar: gewoon opnieuw vragen.
    }
    const destination = /belgi/i.test(address) ? address : `${address}, België`;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = setTimeout(() => controller && controller.abort(), ROUTE_TIMEOUT);
    try {
      const response = await Promise.race([
        fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': key,
            'X-Goog-FieldMask': 'routes.duration,routes.distanceMeters',
          },
          body: JSON.stringify({
            origin: { address: depot },
            destination: { address: destination },
            travelMode: 'DRIVE',
            languageCode: 'nl-BE',
            regionCode: 'be',
          }),
          signal: controller ? controller.signal : undefined,
        }),
        new Promise((resolve, reject) => setTimeout(() => reject(new Error('time-out')), ROUTE_TIMEOUT)),
      ]);
      if (!response.ok) return null;
      const data = await response.json();
      const route = data && data.routes && data.routes[0];
      const seconds = route ? parseFloat(String(route.duration || '').replace(/s$/, '')) : NaN;
      const result = Number.isFinite(seconds) && seconds > 0 ? { seconds, meters: Number(route.distanceMeters) || 0 } : { notFound: true };
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(result));
      } catch (e) {
        // Niet onthouden is geen probleem.
      }
      return result.seconds ? result : null;
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };
  // Rijtijd heen en terug per lading uit een rit: 2 × rijtijd heen; de bovenkant met de verkeersmarge erbij (standaard 0).
  const routeDrive = (route) => {
    const margin = num(estimateConfig.trafficMargin);
    const lo = (2 * route.seconds) / 3600;
    return [lo, lo * (1 + Math.max(0, margin === null ? 0 : margin) / 100)];
  };

  const service = (flag) => {
    const el = chosenService();
    return Boolean(el && el.dataset[flag] === '1');
  };
  const hasDelivery = () => service('delivery') || service('placement');
  const hasPickup = () => service('pickup');

  // Berekening per onderdeel (huur, transport per richting of zelf afhalen/terugbrengen); getoond wordt enkel het totaal.
  // route = rit van Google ({ seconds, meters }); zonder rit geldt de geschatte rijtijd.
  const computeEstimate = (route) => {
    const entered = plateCount();
    const dayPrice = num(estimateConfig.dayPrice);
    const knownDays = rentalDays();
    const openEnded = knownDays === null || knownDays < 1;
    const days = openEnded ? minDays : Math.max(knownDays, minDays);
    if (!entered || dayPrice === null || !days) return null;
    // Minimum aantal platen (enkel als ingesteld): er wordt met het minimum gerekend.
    const plates = minPlates ? Math.max(entered, Math.ceil(minPlates)) : entered;

    const delivery = hasDelivery();
    const pickup = hasPickup();
    let lo = plates * days * dayPrice;
    let hi = lo;

    // Transport: ladingen = platen ÷ platen per lading (naar boven). Per lading en per richting (levering, ophaling):
    // (rijtijd heen en terug + laden/lossen op het depot + uren op locatie) × uurtarief. Plaatsing kost niets extra.
    let loads = 0;
    const perLoad = num(estimateConfig.platesPerLoad);
    if (delivery || pickup) {
      const drive = route ? routeDrive(route) : fallbackDrive();
      const rate = num(estimateConfig.hourRate);
      const depotHours = num(estimateConfig.depotHours);
      const siteHours = num(estimateConfig.siteHours);
      if (!drive || depotHours === null || siteHours === null || rate === null || !(perLoad > 0)) return null;
      loads = Math.ceil(plates / perLoad);
      const legs = loads * ((delivery ? 1 : 0) + (pickup ? 1 : 0));
      lo += legs * (drive[0] + depotHours + siteHours) * rate;
      hi += legs * (drive[1] + depotHours + siteHours) * rate;
    }
    const pickupPrice = num(estimateConfig.pickupPrice);
    const returnPrice = num(estimateConfig.returnPrice);
    if (!delivery) {
      if (pickupPrice === null) return null;
      lo += plates * pickupPrice;
      hi += plates * pickupPrice;
    }
    if (!pickup) {
      if (returnPrice === null) return null;
      lo += plates * returnPrice;
      hi += plates * returnPrice;
    }

    lo = round2(lo);
    hi = round2(hi);
    // Altijd een range: onderkant naar beneden, bovenkant naar boven afgerond, met de grote stap (50) vanaf de
    // drempel (ruwe bovenkant ≥ 500) en anders de kleine stap (25). Is de range smaller dan het minimumverschil
    // (75), dan wordt de bovenkant onderkant + minimumverschil (weer naar boven afgerond). De onderkant is nooit
    // lager dan de kleine stap.
    const bigStep = num(estimateConfig.roundBig) || 50;
    const smallStep = num(estimateConfig.roundSmall) || 25;
    const threshold = num(estimateConfig.roundThreshold);
    const minSpread = num(estimateConfig.minSpread);
    const step = hi >= (threshold === null ? 500 : threshold) ? bigStep : smallStep;
    lo = Math.max(smallStep, Math.floor(lo / step) * step);
    hi = Math.ceil(hi / step) * step;
    const spread = minSpread === null ? 75 : minSpread;
    if (hi - lo < spread) hi = Math.ceil((lo + spread) / step) * step;
    return { lo, hi, openEnded, days, dayPrice, plates, loads, perLoad, minApplied: openEnded || knownDays < minDays };
  };

  // Telt elke berekening; een wijziging in het formulier maakt een lopende Google-aanvraag ongeldig.
  let estimateRun = 0;
  const clearEstimate = () => {
    estimateRun += 1;
    estimateLine = '';
    if (estimateResult && estimateResult.childNodes.length) estimateResult.replaceChildren();
  };

  const driveText = (seconds) => {
    const minutes = Math.max(1, Math.round(seconds / 60));
    if (minutes < 60) return `${minutes} minuten`;
    const rest = minutes % 60;
    return `${Math.floor(minutes / 60)} uur${rest ? ` en ${rest} minuten` : ''}`;
  };

  // Hooguit één Google-aanvraag per klik: de knop is uitgeschakeld zolang de aanvraag loopt.
  const showEstimate = async (event) => {
    if (!estimateResult) return;
    const button = event && event.currentTarget;
    const el = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const run = ++estimateRun;
    const address = val('contact[Werfadres]');
    const useMaps = Boolean(String(estimateConfig.mapsKey || '').trim()) && (hasDelivery() || hasPickup());
    let route = null;
    if (useMaps && address) {
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      try {
        route = await fetchRoute(address);
      } finally {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
      if (run !== estimateRun) return;
    }
    let result = null;
    try {
      result = computeEstimate(route);
    } catch (e) {
      result = null;
    }
    estimateLine = '';
    const card = el('div', 'sp-estimate__card');
    if (!result) {
      card.append(el('p', 'sp-estimate__incomplete', estimateResult.dataset.spIncomplete));
      estimateResult.replaceChildren(card);
      return;
    }
    const total = range(result.lo, result.hi);
    const fill = (text) => (text || '').replace(/\{dagen\}/g, String(result.days)).replace(/\{prijs\}/g, money(result.dayPrice));
    let note = '';
    if (result.openEnded) note = fill(estimateResult.dataset.spOpenNote);
    else if (result.minApplied) note = fill(estimateResult.dataset.spMinNote);
    card.append(el('p', 'sp-estimate__title', estimateResult.dataset.spHeading));
    card.append(el('p', 'sp-estimate__total', total));
    if (note) card.append(el('p', 'sp-estimate__note', note));
    // Rijtijd: met een sleutel de afstand en rijtijd heen van Google, of een zin dat de rijtijd geschat is.
    let driveNote = '';
    if (route) {
      driveNote = (estimateResult.dataset.spRouteNote || '')
        .replace(/\{km\}/g, String(Math.max(1, Math.round(route.meters / 1000))))
        .replace(/\{rijtijd\}/g, driveText(route.seconds));
    } else if (useMaps) {
      driveNote = estimateResult.dataset.spFallbackNote || '';
    }
    if (driveNote) card.append(el('p', 'sp-estimate__note', driveNote));
    if (result.loads > 1 && estimateResult.dataset.spLoadsNote) {
      const loadsNote = estimateResult.dataset.spLoadsNote
        .replace(/\{ladingen\}/g, String(result.loads))
        .replace(/\{platen\}/g, String(result.perLoad));
      card.append(el('p', 'sp-estimate__note', loadsNote));
    }
    card.append(el('p', 'sp-estimate__disclaimer', estimateResult.dataset.spDisclaimer));
    estimateResult.replaceChildren(card);
    const notes = [];
    if (result.minApplied) notes.push(`berekend op minimaal ${result.days} dagen`);
    if (result.loads) notes.push(`${result.loads} ${result.loads === 1 ? 'lading' : 'ladingen'}`);
    estimateLine = `Richtprijs (indicatief, excl. btw): ${total}${notes.length ? ` (${notes.join(', ')})` : ''}`;
  };

  const composeBody = () => {
    const unit = (form.querySelector('[data-sp-amount-unit]:checked') || {}).value || '';
    const amount = val('contact[Hoeveelheid]');
    const services = chosenService();
    const type = val('contact[Klanttype]');
    const company = val('contact[Bedrijf]');
    const vat = val('contact[BTW-nummer]');
    const from = date(val('contact[Vanaf]'));
    const to = val('contact[Einddatum onbekend]') ? 'nog niet gekend' : date(val('contact[Tot]'));

    let period = 'niet opgegeven';
    if (from || to) period = `vanaf ${from || '?'} tot ${to || '?'}`;

    return [
      'OFFERTEAANVRAAG VIA DE WEBSITE',
      '',
      '1. WAT',
      `Plaattype: ${val('contact[Plaattype]') || 'niet opgegeven'}`,
      `Hoeveelheid: ${amount ? `${amount} ${unit}` : 'niet opgegeven'}`,
      ...(form.elements.namedItem('contact[Huren of kopen]')
        ? [`Huren of kopen: ${val('contact[Huren of kopen]') || 'niet opgegeven'}`]
        : []),
      '',
      '2. WANNEER EN WAAR',
      `Periode: ${period}`,
      `Diensten: ${services ? services.value : 'niet gekozen'}`,
      `Werfadres: ${val('contact[Werfadres]').replace(/\s*\n\s*/g, ', ')}`,
      '',
      '3. CONTACT',
      `Naam: ${val('contact[name]')}`,
      `Klant: ${type}${type === 'Bedrijf' && company ? ` – ${company}` : ''}`,
      ...(type === 'Bedrijf' ? [`BTW-nummer: ${vat || 'niet opgegeven'}`] : []),
      `E-mail: ${val('contact[email]')}`,
      `Telefoon: ${val('contact[phone]')}`,
      '',
      ...(estimateLine ? [estimateLine, ''] : []),
      'BERICHT',
      val('contact[Bericht]') || '–',
      '',
      `Privacy: ${val('contact[Privacy]') ? 'akkoord' : 'niet aangevinkt'}`,
    ].join('\n');
  };

  // Alleen naam, e-mail, telefoon en het samengevoegde bericht gaan naar Shopify. De andere velden
  // verliezen tijdelijk hun name, maar blijven bruikbaar (bv. als Shopify's spamcontrole de verzending ophoudt).
  const stripNames = () => {
    Array.from(form.elements).forEach((el) => {
      if (el.name && !KEEP_NAMES.includes(el.name)) {
        el.dataset.spName = el.name;
        el.removeAttribute('name');
      }
    });
    namesStripped = true;
  };
  const restoreNames = () => {
    if (!form) return;
    form.querySelectorAll('[data-sp-name]').forEach((el) => {
      el.name = el.dataset.spName;
      delete el.dataset.spName;
    });
    namesStripped = false;
  };

  if (form && steps.length) {
    // Met JavaScript valideren we per stap; zonder JavaScript werkt de gewone browservalidatie.
    form.noValidate = true;

    if (!formError) restoreState();
    else {
      restoreState();
      current = steps.length;
    }

    syncAmountUnit();
    syncCustomerType();
    syncDates();
    checkServices();
    checkPeriod();
    showStep(current);

    const resetEstimate = (event) => {
      const step = event.target.closest && event.target.closest('[data-sp-step]');
      if (step && step.dataset.spStep !== String(steps.length)) clearEstimate();
    };
    form.addEventListener('input', resetEstimate);
    form.addEventListener('change', resetEstimate);
    if (estimateBox) {
      estimateBox.querySelector('[data-sp-estimate-button]').addEventListener('click', showEstimate);
    }

    form.addEventListener('change', (event) => {
      const t = event.target;
      if (t.matches('[data-sp-amount-unit]')) syncAmountUnit();
      if (t.matches('[data-sp-customer-type]')) syncCustomerType();
      if (t === noEnd || t === fromInput) syncDates();
      if (serviceInputs.includes(t)) checkServices();
      if (t === noEnd || t === fromInput || t === toInput) {
        checkPeriod(toInput.getAttribute('aria-invalid') === 'true');
        if (toInput.checkValidity()) toInput.setAttribute('aria-invalid', 'false');
      }
      saveState();
    });
    form.addEventListener('input', (event) => {
      if (event.target === vatInput) checkVat(event.target.getAttribute('aria-invalid') === 'true');
      if (event.target === toInput || event.target === fromInput) checkPeriod(toInput.getAttribute('aria-invalid') === 'true');
      if (event.target.getAttribute('aria-invalid') === 'true' && event.target.checkValidity()) {
        event.target.setAttribute('aria-invalid', 'false');
      }
    });

    nextBtn.addEventListener('click', () => {
      if (!validateStep(current)) return;
      showStep(current + 1, { focus: true });
      saveState();
    });
    prevBtn.addEventListener('click', () => {
      showStep(current - 1, { focus: true });
      saveState();
    });

    // Enter in een tekstveld gaat naar de volgende stap in plaats van te verzenden.
    form.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.target.tagName === 'TEXTAREA' || event.target.type === 'submit' || event.target.type === 'button') return;
      if (current < steps.length) {
        event.preventDefault();
        nextBtn.click();
      }
    });

    // Eerst valideren (capture op window): een onvolledige aanvraag stopt hier, zodat de focus op het foute veld
    // blijft. Geldige aanvragen gaan ongewijzigd verder via Shopify (inclusief de spamcontrole).
    window.addEventListener(
      'submit',
      (event) => {
        if (event.target !== form) return;
        restoreNames();
        for (let n = 1; n <= steps.length; n += 1) {
          if (!validateStep(n)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
        }
      },
      true
    );

    form.addEventListener('submit', (event) => {
      restoreNames();
      for (let n = 1; n <= steps.length; n += 1) {
        if (!validateStep(n)) {
          event.preventDefault();
          return;
        }
      }
      saveState();
      form.querySelector('[data-sp-body]').value = composeBody();
      stripNames();
      submitBtn.setAttribute('aria-busy', 'true');
    });

    // Gaat de gebruiker na een verzendpoging opnieuw iets doen in het formulier (bv. nadat Shopify's
    // spamcontrole de verzending ophield), dan krijgen alle velden hun name terug.
    const resume = () => {
      if (!namesStripped) return;
      restoreNames();
      submitBtn.removeAttribute('aria-busy');
    };
    form.addEventListener('focusin', (event) => event.target !== submitBtn && resume());
    form.addEventListener('pointerdown', (event) => !event.target.closest('[data-sp-submit]') && resume());

    // Terug-knop van de browser (bfcache): velden opnieuw bruikbaar maken.
    window.addEventListener('pageshow', () => {
      restoreNames();
      syncDates();
      syncCustomerType();
      submitBtn.removeAttribute('aria-busy');
    });
  }

  /* Openen en sluiten */

  const prefillPlate = (plate) => {
    const select = form && form.querySelector('#SpQ-plate');
    // Eén plaattype: verborgen veld met het type uit de Thema-instellingen, niets te kiezen.
    if (!select || !plate || select.tagName !== 'SELECT') return;
    select.value = addPlateOption(select, plate);
    saveState();
  };

  document.addEventListener('click', async (event) => {
    const opener = event.target.closest('[data-sp-quote-open]');
    if (!opener) return;
    event.preventDefault();
    if (menu && menu.isOpen) await menu.close({ instant: true, restoreFocus: false });
    prefillPlate(opener.dataset.spPlate);
    if (opener.dataset.spPlate && steps.length) showStep(1);
    quote.open(opener);
  });

  quoteDialog.querySelectorAll('[data-sp-quote-close]').forEach((btn) => btn.addEventListener('click', () => quote.close()));
  quoteDialog.addEventListener('sp:close', saveState);
  quoteDialog.addEventListener('sp:open', () => tab && tab.classList.remove('is-visible'));
  window.addEventListener('pagehide', saveState);

  /* Na verzenden: Shopify stuurt terug met ?contact_posted=true */

  const params = new URLSearchParams(window.location.search);
  if (params.get('contact_posted') === 'true' || success) {
    storage.clear();
    quote.open();
    if (success) success.focus();
    params.delete('contact_posted');
    const query = params.toString();
    history.replaceState(history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  } else if (formError) {
    quote.open();
    formError.focus();
  }

  /* Zijtabje op desktop: verschijnt na ongeveer de helft van de pagina */

  if (tab) {
    tab.hidden = false;
    const threshold = (parseFloat(tab.dataset.spThreshold) || 25) / 100;
    let ticking = false;
    const updateTab = () => {
      ticking = false;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const past = scrollable > 200 && window.scrollY / scrollable >= threshold;
      tab.classList.toggle('is-visible', past && !quote.isOpen);
      tab.inert = !past;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(updateTab);
        }
      },
      { passive: true }
    );
    quoteDialog.addEventListener('sp:close', () => setTimeout(updateTab, 450));
    updateTab();
  }
})();
